import { Audio, useMutationTranscribeAudio } from "@apis:audio/audio";
import { BotTasks, useQueryBotTask } from "@apis:bot_tasks/bot_tasks";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useBotSelection } from "@hooks:useBotSelection";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { languages } from "@pages:app/audio/components/language";
import { Results } from "@pages:app/audio/components/Results";
import { botTaskAudioSelector } from "@pages:app/audio/components/selector";
import { LANGUAGE_AUTO_DETECT } from "@pages:app/audio/const";
import { FountainBlue } from "@themes:palette";
import { formatFileSize } from "@utils:file";
import { get } from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { LuFileAudio } from "react-icons/lu";
import { MdCancel } from "react-icons/md";
import { RxUpload } from "react-icons/rx";

interface UploadTabProps {
  sx?: SxProps;
}

export const UploadTab = ({ sx }: UploadTabProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [language, setLanguage] = useState<string>(LANGUAGE_AUTO_DETECT);
  const [taskId, setTaskId] = useState<string>("");
  const [taskStatus, setTaskStatus] = useState<
    BotTasks["GET"]["Response"] | undefined
  >();
  const [isPolling, setIsPolling] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);
  const [activeStep, setActiveStep] = useState<number>(0);

  // Steps for the stepper
  const steps = ["Upload", "Processing", "Result"];

  const { getSelectedBot } = useBotSelection();

  const selectedBot = getSelectedBot();

  const transcribeAudioMutation = useMutationTranscribeAudio(
    selectedBot?.id as string,
    {
      onSuccess: (data) => {
        setTaskId(data.id);
        setIsPolling(true);
        setIsUploading(false);
      },
      onError: (error) => {
        setError(
          get(
            error,
            "response.data.detail",
            "Failed to upload file. Please try again.",
          ),
        );
        setIsUploading(false);
      },
    },
  );

  const { refetch: fetchBotTask } = useQueryBotTask(taskId, {
    enabled: false,
    select: botTaskAudioSelector,
  });

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type.startsWith("audio/")) {
        setFile(selectedFile);
        setError("");
      } else {
        setError("Please select a valid audio file");
      }
    }
  };

  const uploadFile = async () => {
    if (!file || !language) {
      setError("Please select both file and language");
      return;
    }

    setIsUploading(true);
    setError("");

    await transcribeAudioMutation.mutateAsync({
      file,
      languageCode:
        language === LANGUAGE_AUTO_DETECT
          ? undefined
          : (language as Audio["POST"]["RequestBody"]["languageCode"]),
    });
  };

  // Polling for task status
  useEffect(() => {
    if (!taskId || !isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data } = await fetchBotTask();
        setTaskStatus(data);

        if (data?.status === "completed" || data?.status === "failed") {
          setIsPolling(false);
        }
      } catch {
        setError("Failed to check status. Please try again.");
        setIsPolling(false);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [taskId, isPolling, fetchBotTask]);

  useEffect(() => {
    if (!taskId) {
      setActiveStep(0); // Upload step
    } else if (
      taskStatus?.status === "completed" ||
      taskStatus?.status === "failed"
    ) {
      setActiveStep(2); // Result step
    } else {
      setActiveStep(1); // Processing step
    }
  }, [taskId, taskStatus]);

  const resetForm = () => {
    setFile(null);
    setLanguage(LANGUAGE_AUTO_DETECT);
    setTaskId("");
    setTaskStatus(undefined);
    setError("");
    setActiveStep(0);
    const fileInput = document.getElementById(
      "audio-upload",
    ) as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Paper elevation={5} sx={{ p: 4, borderRadius: 4, ...sx }}>
      <Box sx={{ mb: 4 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => {
            const stepProps: { completed?: boolean } = {};
            if (activeStep === 2) {
              stepProps.completed = true;
            }

            return (
              <Step key={label} {...stepProps}>
                <StepLabel>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {!taskId ? (
        <Stack spacing={1}>
          <SuperInput.Autocomplete
            disablePortal
            clearOnBlur={false}
            options={languages}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            renderInput={(params) => {
              return <TextField {...params} />;
            }}
            value={languages.find((lang) => lang.value === language) || null}
            onChange={(_, newValue) => {
              setLanguage(newValue ? newValue.value : "autodetect");
            }}
            noOptionsText={"Language does not support yet"}
            width="100%"
            label="Select language"
          />

          <Box>
            <Typography
              gutterBottom
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "500",
                lineHeight: "1.5rem",
              }}
            >
              Audio File
            </Typography>
            <Box position="relative">
              <input
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                id="audio-upload"
              />
              <label htmlFor="audio-upload">
                <Button
                  component="span"
                  variant="outlined"
                  fullWidth
                  startIcon={<RxUpload />}
                  sx={{
                    height: "8rem",
                    display: "flex",
                    flexDirection: "column",
                    border: "2px dashed rgba(0, 0, 0, 0.23)",
                    "&:hover": { border: "2px dashed rgba(0, 0, 0, 0.5)" },
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Click to upload audio file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    MP3, WAV, M4A, etc.
                  </Typography>
                </Button>
              </label>
            </Box>
          </Box>

          {file && (
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: "rgba(0, 0, 0, 0.03)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <LuFileAudio color="action" fontSize="20px" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setFile(null)}>
                  <MdCancel fontSize="large" />
                </IconButton>
              </Stack>
            </Paper>
          )}

          {error && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ color: "error.main" }}
            >
              <MdCancel fontSize="large" />
              <Typography variant="body2">{error}</Typography>
            </Stack>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={uploadFile}
            disabled={!file || !language || isUploading || !selectedBot?.id}
            startIcon={
              isUploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RxUpload />
              )
            }
            fullWidth
            sx={{ py: 2.5, marginTop: "20px !important" }}
          >
            {isUploading ? "Uploading..." : "Upload Audio"}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0, 0, 0, 0.03)" }}
          >
            <Stack direction="row" alignItems="center">
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Task ID
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      "&:hover": { color: `${FountainBlue["500"]} !important` },
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      await handleCopy(taskId);
                    }}
                  >
                    {taskId}
                  </Typography>
                  <SVGInternalIcon
                    icon="copiedIcon"
                    sx={{
                      visibility: isCopied ? "visible" : "hidden",
                      fontSize: "small",
                      color: FountainBlue["500"],
                    }}
                  />
                </Stack>
              </Box>
              <Box
                sx={{
                  marginLeft: "40px",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  File
                </Typography>
                <Typography variant="body2">
                  {file?.name} ({formatFileSize(file?.size || 0)})
                </Typography>
              </Box>
              <Box
                sx={{
                  marginLeft: "60px",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Language
                </Typography>
                <Typography variant="body2">
                  {languages.find((lang) => lang.value === language)?.label ||
                    "Auto Detect"}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {taskStatus && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {taskStatus.status === "submitted" && (
                  <>
                    <SVGInternalIcon
                      icon="time"
                      sx={{ color: "orange", fontSize: "medium" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Submitted - Waiting in queue...
                    </Typography>
                  </>
                )}
                {taskStatus.status === "started" && (
                  <>
                    <CircularProgress size={15} />
                    <Typography variant="body2" color="text.secondary">
                      Processing audio...
                    </Typography>
                  </>
                )}
                {taskStatus.status === "completed" && (
                  <>
                    <SVGInternalIcon
                      icon="circleCheck"
                      sx={{ color: "green", fontSize: "medium" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Transcription completed
                    </Typography>
                  </>
                )}
                {taskStatus.status === "failed" && (
                  <>
                    <SVGInternalIcon
                      icon="outlineCancel"
                      sx={{ color: "red", fontSize: "medium" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Processing failed
                    </Typography>
                  </>
                )}
              </Stack>

              {taskStatus.status === "completed" && (
                <Results
                  transcription={get(taskStatus, "transcription")}
                  diarization={get(taskStatus, "diarization")}
                  sx={{ mt: 3 }}
                />
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={resetForm}
                  sx={{ flexGrow: 1, py: 2.5 }}
                >
                  Upload Another File
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Paper>
  );
};
